export type MyVaultObjectType = "table" | "view" | "canvas" | "row";
export type MyVaultChangeAction = "created" | "updated" | "deleted";

export interface MyVaultChangedObject {
  type: MyVaultObjectType;
  id: string;
  action: MyVaultChangeAction;
  tableId?: string;
  viewId?: string;
}

export interface WorkspaceObjectChange {
  workspaceId: string;
  changedObjects: MyVaultChangedObject[];
  scope?: "workspace";
}

type WorkspaceObjectChangeListener = (change: WorkspaceObjectChange) => void;
const listeners = new Map<string, Set<WorkspaceObjectChangeListener>>();

export function subscribeWorkspaceObjectChanges(
  workspaceId: string,
  listener: WorkspaceObjectChangeListener,
): () => void {
  if (!workspaceId) return () => {};
  const workspaceListeners = listeners.get(workspaceId) ?? new Set();
  workspaceListeners.add(listener);
  listeners.set(workspaceId, workspaceListeners);
  return () => {
    workspaceListeners.delete(listener);
    if (workspaceListeners.size === 0) listeners.delete(workspaceId);
  };
}

export function emitWorkspaceObjectChange(change: WorkspaceObjectChange): void {
  if (!change.workspaceId) return;
  for (const listener of listeners.get(change.workspaceId) ?? []) {
    try {
      listener(change);
    } catch (error) {
      console.warn(
        "MyVault workspace object-change subscriber failed:",
        error instanceof Error ? error.name : "UnknownError",
      );
    }
  }
}

export function emitMyVaultToolChange(
  workspaceId: string,
  toolName: string,
  args: Record<string, unknown>,
  result: unknown,
): boolean {
  const change = getMyVaultChangedObjects(workspaceId, toolName, args, result);
  if (!change) return false;
  emitWorkspaceObjectChange(change);
  return true;
}

export function workspaceChangeAffects(
  change: WorkspaceObjectChange,
  types: MyVaultObjectType[],
  id?: string | number | null,
): boolean {
  if (change.scope === "workspace") {
    return types.includes("table") || types.includes("row");
  }
  const targetId = id === null || id === undefined ? null : String(id);
  return change.changedObjects.some(
    (object) =>
      types.includes(object.type) &&
      (!targetId || object.id === targetId || object.tableId === targetId),
  );
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const stringId = (value: unknown): string | null =>
  typeof value === "string" || typeof value === "number" ? String(value) : null;

function findId(
  record: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const key of keys) {
    const id = stringId(record[key]);
    if (id) return id;
  }
  return null;
}

function addChangedObject(
  objects: MyVaultChangedObject[],
  type: MyVaultObjectType,
  id: string | null,
  action: MyVaultChangeAction,
  related?: Pick<MyVaultChangedObject, "tableId" | "viewId">,
): void {
  if (
    id &&
    !objects.some((object) => object.type === type && object.id === id)
  ) {
    objects.push({ type, id, action, ...related });
  }
}

/** Convert the existing tool-end arguments/result into typed refresh targets. */
export function getMyVaultChangedObjects(
  workspaceId: string,
  toolName: string,
  args: Record<string, unknown>,
  toolResult: unknown,
): WorkspaceObjectChange | null {
  const envelope = asRecord(toolResult);
  if (!envelope || envelope.success === false) return null;
  const result = asRecord(envelope.result) ?? envelope;
  const objects: MyVaultChangedObject[] = [];

  const explicitChanges = [envelope.changed_objects, result.changed_objects];
  for (const explicit of explicitChanges) {
    if (!Array.isArray(explicit)) continue;
    for (const item of explicit) {
      const record = asRecord(item);
      const type = record?.type;
      if (
        record &&
        (type === "table" ||
          type === "view" ||
          type === "canvas" ||
          type === "row")
      ) {
        const action = record.action;
        addChangedObject(
          objects,
          type,
          findId(record, "id"),
          action === "created" || action === "deleted" ? action : "updated",
          {
            tableId: findId(record, "table_id") ?? undefined,
            viewId: findId(record, "view_id") ?? undefined,
          },
        );
      }
    }
  }

  // Canvas tools may have names that are generated from an alias and do not
  // follow the workspace-tool naming convention. Their explicit result
  // metadata is authoritative, so handle it before any name-based inference.
  if (objects.length > 0) {
    return { workspaceId, changedObjects: objects };
  }

  if (toolName === "clear_all_tables" && result.scope === "workspace") {
    return { workspaceId, changedObjects: [], scope: "workspace" };
  }
  if (
    objects.length === 0 &&
    !/^(create|update|delete|drop|add|insert|publish|unpublish)_/.test(toolName)
  ) {
    return null;
  }

  const actionFor = (
    verb: "create" | "update" | "delete" | "drop",
  ): MyVaultChangeAction =>
    verb === "create"
      ? "created"
      : verb === "delete" || verb === "drop"
        ? "deleted"
        : "updated";

  if (toolName.includes("canvas")) {
    const verb = toolName.startsWith("create")
      ? "create"
      : toolName.startsWith("delete")
        ? "delete"
        : "update";
    addChangedObject(
      objects,
      "canvas",
      findId(result, "canvas_id", "canvasId") ??
        findId(args, "canvas_id", "canvasId"),
      actionFor(verb),
    );
  } else if (toolName.includes("view")) {
    const verb = toolName.startsWith("create")
      ? "create"
      : toolName.startsWith("delete")
        ? "delete"
        : "update";
    addChangedObject(
      objects,
      "view",
      findId(result, "view_id", "viewId") ?? findId(args, "view_id", "viewId"),
      actionFor(verb),
    );
  } else if (toolName === "clear_all_tables") {
    return result.scope === "workspace"
      ? { workspaceId, changedObjects: [], scope: "workspace" }
      : null;
  } else if (toolName.includes("row")) {
    const verb =
      toolName.startsWith("insert") || toolName.startsWith("create")
        ? "create"
        : toolName.startsWith("delete")
          ? "delete"
          : "update";
    const viewId =
      findId(args, "view_id", "viewId") ?? findId(result, "view_id", "viewId");
    const rowResult = asRecord(result.row);
    addChangedObject(
      objects,
      "row",
      findId(result, "row_id", "rowId") ??
        findId(rowResult ?? {}, "row_id", "rowId", "id") ??
        findId(args, "row_id", "rowId"),
      actionFor(verb),
      { viewId: viewId ?? undefined },
    );
  } else if (toolName === "create_table" || toolName === "drop_table") {
    addChangedObject(
      objects,
      "table",
      findId(result, "table_id", "tableId") ??
        findId(args, "table_id", "tableId"),
      actionFor(toolName === "create_table" ? "create" : "drop"),
    );
  } else if (toolName === "add_column") {
    addChangedObject(
      objects,
      "table",
      findId(args, "table_id", "tableId") ??
        findId(result, "table_id", "tableId"),
      "updated",
    );
  } else if (toolName === "create_relationship") {
    for (const tableId of [
      findId(result, "from_table_id"),
      findId(result, "to_table_id"),
    ]) {
      addChangedObject(objects, "table", tableId, "updated");
    }
  }

  return objects.length ? { workspaceId, changedObjects: objects } : null;
}
